<template>
  <AdminLayout>
    <template #breadcrumb>
      <breadcrumb-link
        view="AdminReimbursementPrefixList"
        label="Reimbursement Prefixes"
        :active="true"
      />
    </template>

    <div class="box" style="position: relative">
      <b-loading :is-full-page="false" v-model="loading"></b-loading>
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <div>
              <h1 class="title mb-2">Manage Reimbursement Prefixes</h1>
              <p class="subtitle is-6 has-text-grey mt-0">Total: {{ totalCount }} prefixes</p>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <b-button type="is-success" @click="openCreateModal">
              <b-icon icon="plus"></b-icon>
              <span>Create New</span>
            </b-button>
          </div>
        </div>
      </div>
      <hr />

      <!-- Search -->
      <b-field>
        <div class="field-body">
          <b-field label="Search prefixes">
            <b-input
              v-model="search"
              placeholder="Search by prefix..."
              icon="magnify"
              @input="debouncedSearch"
            ></b-input>
          </b-field>
        </div>
      </b-field>

      <div class="table-container">
        <b-table
          :data="prefixes"
          :loading="loading"
          hoverable
          striped
          :paginated="true"
          backend-pagination
          :total="totalCount"
          :per-page="perPage"
          :current-page="currentPage"
          @page-change="onPageChange"
          pagination-position="bottom"
          default-sort="prefix"
          default-sort-direction="asc"
        >
          <b-table-column field="prefix" label="Prefix" v-slot="props" sortable>
            <span class="has-text-weight-semibold is-family-monospace">{{ props.row.prefix }}</span>
          </b-table-column>

          <b-table-column field="is_local_allowable" label="Local Allowable" v-slot="props" centered width="150">
            <b-tag :type="props.row.is_local_allowable ? 'is-info' : 'is-light'">
              {{ props.row.is_local_allowable ? 'Yes' : 'No' }}
            </b-tag>
          </b-table-column>

          <b-table-column label="Actions" width="160" v-slot="props">
            <div class="field is-grouped">
              <div class="control">
                <b-button
                  type="is-primary is-outlined"
                  size="is-small"
                  icon-left="pencil"
                  @click="openEditModal(props.row)"
                >
                  Edit
                </b-button>
              </div>
              <div class="control">
                <b-button
                  type="is-danger"
                  size="is-small"
                  icon-left="delete"
                  @click="confirmDelete(props.row)"
                >
                  Delete
                </b-button>
              </div>
            </div>
          </b-table-column>

          <template #empty>
            <div class="has-text-centered">
              <b-icon icon="emoticon-sad" size="is-large"></b-icon>
              <p>No prefixes found.</p>
            </div>
          </template>
        </b-table>
      </div>

      <!-- Create Modal -->
      <b-modal v-model="isCreateModalActive" :width="500" scroll="keep">
        <form @submit.prevent="createPrefix">
          <div class="modal-card">
            <header class="modal-card-head">
              <p class="modal-card-title">Create New Prefix</p>
            </header>
            <section class="modal-card-body">
              <b-loading :is-full-page="false" v-model="isCreating"></b-loading>

              <b-field label="Prefix" required>
                <b-input
                  v-model="createForm.prefix"
                  placeholder="Enter prefix code"
                  required
                  maxlength="10"
                ></b-input>
              </b-field>

              <b-field>
                <b-checkbox v-model="createForm.is_local_allowable">
                  Mark as Local Allowable
                </b-checkbox>
              </b-field>
              <p class="help">
                When marked as local allowable, the reimbursement estimate will show "Local Allowable Rates" text if data is available.
              </p>
            </section>
            <footer class="modal-card-foot">
              <b-button @click="closeCreateModal">Cancel</b-button>
              <b-button type="is-success" native-type="submit" :loading="isCreating">
                Create
              </b-button>
            </footer>
          </div>
        </form>
      </b-modal>

      <!-- Edit Modal -->
      <b-modal v-model="showEditModal" :width="500" scroll="keep">
        <form @submit.prevent="handleUpdatePrefix">
          <div class="modal-card" style="width: auto">
            <header class="modal-card-head">
              <p class="modal-card-title">Edit Prefix</p>
              <button type="button" class="delete" @click="showEditModal = false"></button>
            </header>
            <section class="modal-card-body">
              <b-loading :is-full-page="false" v-model="editLoading"></b-loading>

              <b-field
                label="Prefix"
                :type="{ 'is-danger': !!errors.prefix }"
                :message="errors.prefix"
              >
                <b-input
                  v-model="editForm.prefix"
                  placeholder="Enter prefix"
                  required
                  maxlength="10"
                ></b-input>
              </b-field>

              <b-field>
                <b-checkbox v-model="editForm.is_local_allowable">
                  Mark as Local Allowable
                </b-checkbox>
              </b-field>
              <p class="help">
                When marked as local allowable, the reimbursement estimate will show "Local Allowable Rates" text if data is available.
              </p>
            </section>
            <footer class="modal-card-foot">
              <b-button @click="showEditModal = false">Cancel</b-button>
              <b-button type="is-primary" native-type="submit" :loading="editLoading">
                Save Changes
              </b-button>
            </footer>
          </div>
        </form>
      </b-modal>
    </div>
  </AdminLayout>
</template>

<script>
import request from '../../request';
import AdminLayout from '../../layouts/AdminLayout.vue';

export default {
  name: 'AdminReimbursementPrefixList',
  components: {
    AdminLayout,
  },
  data() {
    return {
      loading: false,
      prefixes: [],
      totalCount: 0,
      currentPage: 1,
      perPage: 50,
      search: '',
      searchTimeout: null,
      isCreateModalActive: false,
      createForm: {
        prefix: '',
        is_local_allowable: false,
      },
      isCreating: false,
      showEditModal: false,
      editLoading: false,
      editingPrefix: null,
      editForm: {},
      errors: {},
    };
  },
  created() {
    this.loadPrefixes();
  },
  methods: {
    async loadPrefixes() {
      this.loading = true;
      try {
        const params = new URLSearchParams();
        if (this.perPage) params.append('limit', this.perPage);
        if (this.currentPage > 1) params.append('page', this.currentPage);
        if (this.search.trim()) params.append('prefix', this.search.trim());

        const response = await request.get(`/admin/reimbursements/prefixes?${params.toString()}`);
        this.prefixes = response.data.prefixes || [];
        this.totalCount = parseInt(response.headers['x-total-count'] || '0');
      } catch (error) {
        console.error('Error loading prefixes:', error);
        this.$buefy.toast.open({
          message: 'Failed to load prefixes',
          type: 'is-danger',
        });
      } finally {
        this.loading = false;
      }
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadPrefixes();
    },

    debouncedSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadPrefixes();
      }, 300);
    },

    openCreateModal() {
      this.createForm = {
        prefix: '',
        is_local_allowable: false,
      };
      this.isCreateModalActive = true;
    },

    closeCreateModal() {
      this.isCreateModalActive = false;
      this.createForm = {
        prefix: '',
        is_local_allowable: false,
      };
    },

    async createPrefix() {
      this.isCreating = true;
      try {
        await request.post('/admin/reimbursements/prefixes', this.createForm);

        this.$buefy.toast.open({
          message: 'Prefix created successfully',
          type: 'is-success',
        });

        this.closeCreateModal();
        this.loadPrefixes();
      } catch (error) {
        console.error('Error creating prefix:', error);
        this.$buefy.toast.open({
          message: error.response?.data?.message || 'Failed to create prefix',
          type: 'is-danger',
        });
      } finally {
        this.isCreating = false;
      }
    },

    openEditModal(prefix) {
      this.editingPrefix = prefix;
      this.editForm = {
        prefix: prefix.prefix,
        is_local_allowable: prefix.is_local_allowable || false,
      };
      this.errors = {};
      this.showEditModal = true;
    },

    async handleUpdatePrefix() {
      this.editLoading = true;
      this.errors = {};

      try {
        const payload = {
          prefix: this.editForm.prefix?.trim(),
          is_local_allowable: this.editForm.is_local_allowable,
        };

        await request.put(`/admin/reimbursements/prefixes/${this.editingPrefix.id}`, payload);

        this.$buefy.toast.open({
          message: 'Prefix updated successfully',
          type: 'is-success',
        });

        this.showEditModal = false;
        this.loadPrefixes();
      } catch (error) {
        console.error('Error updating prefix:', error);
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else if (error.response?.data?.message) {
          this.$buefy.toast.open({
            message: error.response.data.message,
            type: 'is-danger',
          });
        } else {
          this.$buefy.toast.open({
            message: 'Failed to update prefix',
            type: 'is-danger',
          });
        }
      } finally {
        this.editLoading = false;
      }
    },

    confirmDelete(prefix) {
      this.$buefy.dialog.confirm({
        title: 'Delete Prefix',
        message: `Are you sure you want to delete prefix "<strong>${prefix.prefix}</strong>"? This action cannot be undone.`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.deletePrefix(prefix),
      });
    },

    async deletePrefix(prefix) {
      try {
        await request.delete(`/admin/reimbursements/prefixes/${prefix.id}`);

        this.$buefy.toast.open({
          message: 'Prefix deleted successfully',
          type: 'is-success',
        });

        this.loadPrefixes();
      } catch (error) {
        console.error('Error deleting prefix:', error);
        let errorMessage = 'Failed to delete prefix';

        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.status === 400) {
          errorMessage = 'Cannot delete this prefix - it may still be in use by reimbursements';
        }

        // Show error message in a dialog for better visibility
        this.$buefy.dialog.alert({
          title: 'Delete Failed',
          message: errorMessage,
          type: 'is-danger',
          hasIcon: true,
          ariaRole: 'alertdialog',
          ariaModal: true,
        });
      }
    },
  },
};
</script>
